/**
 * The Lab's building blocks, on the Statistics card language: one card shell, one headline,
 * one stat tile, one status badge, so every results section reads as the same product.
 */
import React from "react";
import { Info } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { cn } from "@/lib/utils";
import { statsCardHeadlineClassName } from "../Statistics/components/statisticsCardHeadline";
import { statisticsShadcnCardClassName } from "../Statistics/components/statisticsShadcnCardShell";

export type StrategyRdTone = "good" | "warn" | "bad" | "neutral" | "brand";

/**
 * Status badges are the app's filled gradient badges (Reports type badges, Long / Short):
 * white text on glacial green, orange, red, grey, or the brand gradient.
 */
// Urbanist sits low in its line box: the padding lifts the text 1px; icons stay centred.
const BADGE_BASE_CLASS =
  "!rounded-[6px] !border-0 font-[family-name:var(--font-primary)] font-medium normal-case tracking-normal !text-white shadow-none !pt-px !pb-[3px] leading-4 [&>svg]:translate-y-px";

const TONE_CLASS_FILLED: Record<StrategyRdTone, string> = {
  good: "bg-[image:var(--gradient-glacial-green)]",
  warn: "bg-[image:var(--gradient-glacial-orange)]",
  bad: "bg-[image:var(--gradient-glacial-red)]",
  neutral: "bg-[image:linear-gradient(135deg,#6b7280,#9ca3af)]",
  brand: "bg-[image:var(--gradient-primary)]",
};

/** Figures: IBM Plex Sans, tabular, the Statistics big-number weight. */
export const strategyRdFigureClassName =
  "font-ibm-plex text-2xl font-semibold leading-none tabular-nums text-gray-600 dark:text-white";

/** Card titles: the Statistics / Dashboard chart-card title (uppercase, muted, text-sm). */
export const strategyRdCardTitleClassName =
  "!m-0 font-[family-name:var(--font-primary)] !text-sm !font-medium !leading-5 uppercase text-muted-foreground dark:!text-white";

/** Section / stat labels: the Statistics card headline (uppercase, muted). */
export const strategyRdCaptionClassName = statsCardHeadlineClassName;

/** Body copy on Lab cards: muted gray, never black. */
export const strategyRdBodyTextClassName =
  "font-[family-name:var(--font-primary)] text-sm leading-snug text-gray-500 dark:text-sidebar-foreground";

/** Every results section's card: the Statistics shell, one padding. */
// The stroke is forced: the app's Chakra reset strips borders from elements without the
// Statistics card hook, and every card must show its outline.
export const strategyRdSectionCardClassName = cn(
  statisticsShadcnCardClassName,
  "gap-4 bg-background p-5 !border !border-solid !border-border"
);

export function StrategyRdLabCard({
  title,
  description,
  actions,
  children,
  className,
  ...rest
}: {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLElement>, "title">): JSX.Element {
  return (
    <section
      data-statistics-shadcn-card=""
      className={cn(strategyRdSectionCardClassName, className)}
      {...rest}
    >
      {title || actions ? (
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {title ? <h3 className={cn(strategyRdCardTitleClassName, "flex items-center gap-1.5")}>{title}</h3> : null}
            {description ? <p className={cn(strategyRdBodyTextClassName, "max-w-[70ch]")}>{description}</p> : null}
          </div>
          {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

/** Tiles separated by hairlines, not boxed one by one. */
export function StrategyRdStatGrid({
  children,
  columns = 4,
  variant = "joined",
  className,
}: {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
  /** `joined`: tiles share hairlines in one box. `cards`: each tile is its own card, spaced apart. */
  variant?: "joined" | "cards";
  className?: string;
}): JSX.Element {
  return (
    // Columns follow the grid's own width (the Lab's results column is much narrower than the
    // window), so tiles never get squeezed until their text breaks.
    <div className="@container/statgrid min-w-0">
      <div
        className={cn(
          variant === "cards"
            ? // Each tile its own card (the Statistics card shell), spaced like the page's cards.
              "grid gap-4 [&>*]:!rounded-md [&>*]:!border [&>*]:!border-solid [&>*]:!border-border [&>*]:!p-5"
            : // Per-tile right / bottom rules (clipped at the edges): an odd number of tiles leaves
              // clean empty space, not a grey block.
              cn(
                "grid overflow-hidden rounded-[6px] border border-border bg-background dark:bg-sidebar",
                "[&>*]:-mb-px [&>*]:-mr-px [&>*]:border-b [&>*]:border-r [&>*]:border-border"
              ),
          "grid-cols-1 @[34rem]/statgrid:grid-cols-2",
          columns === 3 && "@[54rem]/statgrid:grid-cols-3",
          columns === 4 && "@[68rem]/statgrid:grid-cols-4",
          className
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function StrategyRdStat({
  label,
  value,
  valueColor,
  unit,
  badge,
  info,
  children,
  className,
  ...rest
}: {
  /** "ⓘ What this means" next to the label (StrategyRdInfo). */
  info?: React.ReactNode;
  label: React.ReactNode;
  value: React.ReactNode;
  valueColor?: string;
  /** Small text after the figure ("losses in a row"). */
  unit?: React.ReactNode;
  badge?: React.ReactNode;
  /** Supporting line(s) under the figure. */
  children?: React.ReactNode;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "children">): JSX.Element {
  return (
    <div className={cn("flex min-w-0 flex-col gap-3 bg-background p-4 dark:bg-sidebar", className)} {...rest}>
      <div className="flex min-h-5 items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1">
          <span className={strategyRdCaptionClassName}>{label}</span>
          {info}
        </span>
        {badge}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1">
        <span className={strategyRdFigureClassName} style={valueColor ? { color: valueColor } : undefined}>
          {value}
        </span>
        {unit ? <span className={strategyRdBodyTextClassName}>{unit}</span> : null}
      </div>
      {children ? (
        <div className="flex flex-col gap-2 font-[family-name:var(--font-primary)] text-xs leading-relaxed text-gray-500 dark:text-sidebar-foreground">
          {children}
        </div>
      ) : null}
    </div>
  );
}

/** A status pill: the app's `Badge`, in the outlined Strategies status variants. */
export function StrategyRdBadge({
  tone = "neutral",
  children,
  title,
  className,
  ...rest
}: {
  tone?: StrategyRdTone;
  children: React.ReactNode;
  title?: string;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLSpanElement>, "children">): JSX.Element {
  return (
    <Badge
      title={title}
      data-strategy-rd-badge={tone}
      className={cn(BADGE_BASE_CLASS, TONE_CLASS_FILLED[tone], className)}
      {...rest}
    >
      {children}
    </Badge>
  );
}

/**
 * One check or group inside a Lab card: a bordered block with a headline, an optional ⓘ,
 * a status badge on the right, then its content. Siblings in a grid get equal heights.
 */
export function StrategyRdSubsection({
  title,
  info,
  badge,
  description,
  children,
  className,
  ...rest
}: {
  title: React.ReactNode;
  info?: React.ReactNode;
  badge?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLElement>, "title" | "children">): JSX.Element {
  return (
    <section
      className={cn(
        "flex min-w-0 flex-col gap-3 !rounded-md !border !border-solid !border-border bg-background p-5 dark:bg-sidebar",
        className
      )}
      {...rest}
    >
      <header className="flex min-h-5 items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1">
          <h4 className="m-0 font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]">
            {title}
          </h4>
          {info}
        </span>
        {badge}
      </header>
      {description ? <p className={cn(strategyRdBodyTextClassName, "text-xs leading-relaxed")}>{description}</p> : null}
      {children}
    </section>
  );
}

export type StrategyRdFact = {
  key?: string;
  label: React.ReactNode;
  value: React.ReactNode;
  valueColor?: string;
};

/** Label / value rows with hairlines between them: the supporting numbers of a stat or check. */
export function StrategyRdFacts({
  rows,
  className,
  ...rest
}: {
  rows: (StrategyRdFact | null | false | undefined)[];
  className?: string;
} & Omit<React.HTMLAttributes<HTMLDListElement>, "children">): JSX.Element | null {
  const items = rows.filter((r): r is StrategyRdFact => Boolean(r));
  if (items.length === 0) return null;
  return (
    // The app's table look (Trade Log / Statistics tables): bordered, muted header row.
    <div
      className={cn("overflow-hidden !rounded-[6px] !border !border-solid !border-border", className)}
      {...(rest as React.HTMLAttributes<HTMLDivElement>)}
    >
      <table className="w-full border-collapse font-[family-name:var(--font-primary)] text-xs">
        <tbody>
          {items.map((row, i) => (
            <tr key={row.key ?? i} className="!border-0 !border-t !border-solid !border-border first:!border-t-0">
              <td className="px-3 py-2 text-gray-500 dark:text-sidebar-foreground">{row.label}</td>
              <td
                className="px-3 py-2 text-right font-ibm-plex font-medium tabular-nums text-gray-600 dark:text-white"
                style={row.valueColor ? { color: row.valueColor } : undefined}
              >
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A quiet footnote at the bottom of a card (method notes, calibration). */
export function StrategyRdNote({
  children,
  className,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLParagraphElement>, "children">): JSX.Element {
  return (
    <p
      className={cn(
        "flex items-start gap-2 border-t border-border pt-3 font-[family-name:var(--font-primary)] text-xs leading-relaxed text-gray-500 dark:text-sidebar-foreground",
        className
      )}
      {...rest}
    >
      <Info className="mt-0.5 size-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

/** One numbered block of a settings form: a step, a title, what it does, then its fields. */
export function StrategyRdSettingsSection({
  step,
  title,
  description,
  actions,
  children,
  className,
  ...rest
}: {
  step?: number;
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
} & Omit<React.HTMLAttributes<HTMLElement>, "title">): JSX.Element {
  // A settings card (Profile settings / Lab cards): brand title, muted description, its fields.
  return (
    <section className={cn(strategyRdSectionCardClassName, className)} data-step={step} {...rest}>
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="m-0 font-[family-name:var(--font-primary)] text-base font-medium text-[color:var(--brand-tropical-surf)]">
              {title}
            </h4>
            {actions}
          </div>
          {description ? <p className={cn(strategyRdBodyTextClassName, "m-0")}>{description}</p> : null}
        </div>
      </header>
      {children}
    </section>
  );
}

/** Lab hover tooltips follow the theme like the info popovers (the shared tooltip inverts:
 *  a white bubble in dark mode). Pass to `TooltipContent` as `className` / `arrowClassName`. */
export const strategyRdTooltipClassName =
  "border border-border bg-popover text-popover-foreground shadow-md";
export const strategyRdTooltipArrowClassName = "bg-popover fill-popover";

/**
 * "ⓘ" next to a concept: a short plain-words explanation of what it is, how to read it and
 * why it matters (hold-out, halves, Monte Carlo, stress tests…).
 */
export function StrategyRdInfo({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`What is ${title}?`}
          className={cn(
            "inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground/70 normal-case tracking-normal transition-colors hover:text-[color:var(--brand-tropical-surf)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-tropical-surf)]/40",
            className
          )}
          data-strategy-rd-info=""
        >
          <Info className="size-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 max-w-[calc(100vw-2rem)] p-4 font-[family-name:var(--font-primary)] text-[13px] leading-relaxed normal-case tracking-normal"
      >
        <p className={cn(statsCardHeadlineClassName, "mb-2")}>{title}</p>
        <div className="flex flex-col gap-1.5 text-gray-500 dark:text-sidebar-foreground [&_strong]:font-semibold [&_strong]:text-gray-600 dark:[&_strong]:text-white">
          {children}
        </div>
      </PopoverContent>
    </Popover>
  );
}
